/**
 * Cryptography utilities for encrypting sensitive data
 * Uses TweetNaCl (NaCl) for authenticated encryption
 */

import nacl from "tweetnacl"
import { decodeUTF8, encodeUTF8, encodeBase64, decodeBase64 } from "tweetnacl-util"

/**
 * Derives a 32-byte encryption key from a session token
 * Uses SHA-256 to create a consistent key from variable-length tokens
 *
 * @param token - Session token (typically JWT)
 * @returns 32-byte Uint8Array suitable for NaCl secretbox
 */
export async function deriveKey(token: string): Promise<Uint8Array> {
  if (!token || token.length === 0) {
    throw new Error("Cannot derive key from empty token")
  }

  const encoder = new TextEncoder()
  const data = encoder.encode(token)
  const hash = await crypto.subtle.digest("SHA-256", data)
  return new Uint8Array(hash)
}

/**
 * Encrypts plaintext data using authenticated encryption (NaCl secretbox)
 *
 * Security properties:
 * - Authenticated encryption (prevents tampering)
 * - Random nonce for each encryption
 * - XSalsa20 stream cipher + Poly1305 MAC
 *
 * @param plaintext - Data to encrypt (will be JSON stringified)
 * @param token - Session token used to derive encryption key
 * @returns Base64-encoded encrypted data (nonce + ciphertext)
 * @throws Error if encryption fails or token is invalid
 */
export async function encrypt(plaintext: unknown, token: string): Promise<string> {
  try {
    const key = await deriveKey(token)
    const nonce = nacl.randomBytes(nacl.secretbox.nonceLength) // 24 bytes
    const messageUint8 = decodeUTF8(JSON.stringify(plaintext))
    const encrypted = nacl.secretbox(messageUint8, nonce, key)

    if (!encrypted) {
      throw new Error("Encryption failed")
    }

    // Prepend nonce to encrypted message
    const fullMessage = new Uint8Array(nonce.length + encrypted.length)
    fullMessage.set(nonce)
    fullMessage.set(encrypted, nonce.length)

    return encodeBase64(fullMessage)
  } catch (error) {
    console.error("Encryption error:", error)
    throw new Error("Failed to encrypt data")
  }
}

/**
 * Decrypts data encrypted with the encrypt() function
 *
 * @param ciphertext - Base64-encoded encrypted data (from encrypt())
 * @param token - Session token used to derive encryption key
 * @returns Decrypted and JSON-parsed data
 * @throws Error if decryption fails (wrong key, tampered data, etc.)
 */
export async function decrypt<T = unknown>(ciphertext: string, token: string): Promise<T> {
  try {
    const key = await deriveKey(token)
    const messageWithNonceAsUint8Array = decodeBase64(ciphertext)

    // Extract nonce and message
    const nonce = messageWithNonceAsUint8Array.slice(0, nacl.secretbox.nonceLength)
    const message = messageWithNonceAsUint8Array.slice(nacl.secretbox.nonceLength)

    // Decrypt and authenticate
    const decrypted = nacl.secretbox.open(message, nonce, key)

    if (!decrypted) {
      throw new Error("Decryption failed - data may be corrupted or key is incorrect")
    }

    const decryptedText = encodeUTF8(decrypted)
    return JSON.parse(decryptedText) as T
  } catch (error) {
    console.error("Decryption error:", error)
    throw new Error("Failed to decrypt data")
  }
}

/**
 * Checks if data appears to be encrypted (base64 with sufficient length)
 * This is a heuristic check, not cryptographically secure
 *
 * @param data - Data to check
 * @returns true if data looks encrypted, false otherwise
 */
export function isEncrypted(data: unknown): boolean {
  if (typeof data !== "string") {
    return false
  }

  // Encrypted data should be base64 and at least nonce length (24 bytes = 32 chars base64)
  const base64Regex = /^[A-Za-z0-9+/]+=*$/
  return base64Regex.test(data) && data.length >= 32
}
